import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformBrowser, isPlatformServer } from '@angular/common';
import { REQUEST } from '@shared/tokens/server.tokens';
import { TranslateService as NGXTranslateService } from '@ngx-translate/core';
import { Meta } from '@angular/platform-browser';
import { AppStorage } from '../shared/storages/universal.inject';

import { ILang } from './translates.interface';
import { LocationManagerService } from '@cakeiteasy/web-utils';
import { firstValueFrom, Observable } from 'rxjs';

const LANG_LIST: ILang[] = [
  { 'code': 'en', 'name': 'English', 'culture': 'en-US' },
  { 'code': 'sv', 'name': 'Swedish', 'culture': 'sv-SE' },
  { 'code': 'da', 'name': 'Danish', 'culture': 'da-DK' },
  { 'code': 'no', 'name': 'Norwegian', 'culture': 'nb-NO', aliases: ['nb', 'nn'] }
];

export const DOMAIN_LANGUAGE_CODE_MAP = {
  'no': 'no',
  'se': 'sv',
  'com': 'en'
};

const LANG_DEFAULT: ILang = LANG_LIST[0];
const STORAGE_LANG_NAME: string = 'cie-lang-code';

@Injectable()
export class TranslatesService {
  constructor(@Inject(PLATFORM_ID) private _platformId: Object,
              private _locationManager: LocationManagerService,
              @Inject(DOCUMENT) private _document: any,
              @Inject(REQUEST) private _request: any,
              @Inject(NGXTranslateService) private _translate: NGXTranslateService,
              private _meta: Meta,
              @Inject(AppStorage) private _appStorage: Storage
  ) {
  }

  public initLanguage(): Promise<any> {
    this._translate.addLangs(LANG_LIST.map((lang: ILang) => lang.code));

    const language: ILang = this._getLanguage();
    return firstValueFrom(this._setLanguage(language));
  }

  public changeLang(code: string): void {
    const lang: ILang = this._findLang(code);
    if (!lang || lang.code === this._translate.currentLang) {
      return;
    }
    this._appStorage.setItem(STORAGE_LANG_NAME, lang.code);
    window.location.reload();
  }

  public getCurrentLang(): string {
    return this._translate.currentLang;
  }

  private _getLanguageCodeBasedOnDomain(domain: string) {
    if (domain) {
      return this._findLang(DOMAIN_LANGUAGE_CODE_MAP[domain]);
    }

    return null;
  }

  private _getLanguage(): ILang {
    let language: ILang = this._findLang(this._appStorage.getItem(STORAGE_LANG_NAME));

    if (language) {
      return language;
    }

    let domain = this._locationManager.getDomainName();

    if (isPlatformBrowser(this._platformId)) {
      language = this._getLanguageCodeBasedOnDomain(domain);
    }

    if (isPlatformServer(this._platformId)) {
      language = this._getLanguageCodeBasedOnDomain(domain);

      if (!language) {
        try {
          const reqLangList: string[] = this._request.headers['accept-language'].split(';')[0].split(',');
          language = LANG_LIST.find((lang: ILang) => reqLangList.indexOf(lang.code) !== -1 || reqLangList.indexOf(lang.culture) !== -1);
        } catch (err) {
          language = LANG_DEFAULT;
        }
      }
    }

    language = language || LANG_DEFAULT;
    this._appStorage.setItem(STORAGE_LANG_NAME, language.code);

    return language;
  }

  private _findLang(code: string): ILang | null {
    return code
      ? LANG_LIST.find((lang: ILang) => lang.code === code || (lang.aliases && lang.aliases.indexOf(code) >= 0))
      : null;
  }

  private _setLanguage(lang: ILang): Observable<any> {
    let obs = this._translate.use(lang.code);

    obs.subscribe(() => {
      this._meta.updateTag({ property: 'og:locale', content: lang.culture }, "property='og:locale'");
      this._document.documentElement.lang = lang.code;
    });

    return obs;
  }
}
